import React, { useEffect, useState, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const Hero = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [particles, setParticles] = useState([]);
  const [heroStats, setHeroStats] = useState([]);
  const heroRef = useRef(null);
  const { language } = useLanguage();

  useEffect(() => {
    // Fetch hero stats from API
    const fetchHeroStats = async () => {
      try {
        const response = await fetch(`${API}/hero-stats`);
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            setHeroStats(data);
          } else {
            // Fallback to default stats
            setHeroStats([
              { id: 1, key: 'projects', value: '500+', order: 1 },
              { id: 2, key: 'clients', value: '200+', order: 2 },
              { id: 3, key: 'years', value: '10+', order: 3 }
            ]);
          }
        }
      } catch (error) {
        console.error('Error fetching hero stats:', error);
        // Fallback to default stats
        setHeroStats([
          { id: 1, key: 'projects', value: '500+', order: 1 },
          { id: 2, key: 'clients', value: '200+', order: 2 },
          { id: 3, key: 'years', value: '10+', order: 3 }
        ]);
      }
    };

    fetchHeroStats();

    // Generate floating particles
    const newParticles = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 2,
      duration: Math.random() * 20 + 15,
      delay: Math.random() * 5
    }));
    setParticles(newParticles);

    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setMousePosition({
          x: (e.clientX - rect.left) / rect.width,
          y: (e.clientY - rect.top) / rect.height
        });
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToClients = () => {
    const element = document.getElementById('clients');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const t = (key) => getTranslation(language, key);

  return (
    <section 
      ref={heroRef}
      id="hero" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-slate-950 py-20 sm:py-0"
    >
      {/* Animated Stars Background */}
      <div className="absolute inset-0">
        <div className="stars-layer stars-small"></div>
        <div className="stars-layer stars-medium"></div>
        <div className="stars-layer stars-large"></div>
      </div>

      {/* 3D Animated Globe - Responsive with better mobile ratios */}
      <div 
        className="absolute inset-0 flex items-center justify-center opacity-15 sm:opacity-20 md:opacity-25 lg:opacity-30"
        style={{
          transform: `rotateY(${(mousePosition.x - 0.5) * 20}deg) rotateX(${(mousePosition.y - 0.5) * -20}deg)`,
          transition: 'transform 0.3s ease-out',
          perspective: '1000px'
        }}
      >
        <div className="relative w-full h-full flex items-center justify-center">
          {/* Globe container with better mobile proportions */}
          <div 
            className="globe-container relative"
            style={{
              width: 'min(85vw, 240px)',
              height: 'min(85vw, 240px)',
              transformStyle: 'preserve-3d'
            }}
          >
            {/* Main Globe Sphere */}
            <div className="globe-sphere">
              {/* Atmosphere glow */}
              <div className="globe-atmosphere"></div>
              
              {/* Continents layer */}
              <div className="globe-continents"></div>
              
              {/* Ocean layer */}
              <div className="globe-ocean"></div>
              
              {/* Clouds layer */}
              <div className="globe-clouds"></div>
              
              {/* Light reflection */}
              <div className="globe-reflection"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 overflow-hidden">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full bg-gradient-to-br from-blue-400 to-pink-400 opacity-30"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              animation: `float ${particle.duration}s ease-in-out infinite`,
              animationDelay: `${particle.delay}s`,
              filter: 'blur(1px)'
            }}
          />
        ))}
      </div>

      {/* Animated Gradient Orbs */}
      <div className="absolute inset-0 opacity-40">
        <div 
          className="absolute w-96 h-96 rounded-full blur-3xl animate-pulse-slow"
          style={{
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, transparent 70%)',
            left: `${20 + mousePosition.x * 10}%`,
            top: `${20 + mousePosition.y * 10}%`,
            transition: 'all 0.3s ease-out'
          }}
        />
        <div 
          className="absolute w-[500px] h-[500px] rounded-full blur-3xl animate-pulse-slow"
          style={{
            background: 'radial-gradient(circle, rgba(236, 72, 153, 0.4) 0%, transparent 70%)',
            right: `${10 + mousePosition.x * 15}%`,
            bottom: `${15 + mousePosition.y * 15}%`,
            transition: 'all 0.3s ease-out',
            animationDelay: '1s'
          }}
        />
        <div 
          className="absolute w-80 h-80 rounded-full blur-3xl animate-pulse-slow"
          style={{
            background: 'radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, transparent 70%)',
            left: `${50 + mousePosition.x * 8}%`,
            top: `${50 + mousePosition.y * 8}%`,
            transition: 'all 0.3s ease-out',
            animationDelay: '2s'
          }}
        />
      </div>

      {/* Geometric Shapes */}
      <div className="absolute inset-0 opacity-10">
        <div 
          className="absolute w-64 h-64 border border-pink-400 rounded-full animate-spin-slow"
          style={{
            left: '10%',
            top: '20%',
            transform: `rotate(${mousePosition.x * 45}deg)`
          }}
        />
        <div 
          className="absolute w-48 h-48 border border-blue-400 rotate-45 animate-pulse-slow"
          style={{
            right: '15%',
            top: '30%',
            transform: `rotate(${45 + mousePosition.y * 30}deg)`
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto w-full pt-20 sm:pt-20 md:pt-24 lg:pt-20">
        <div className="space-y-3 sm:space-y-3 md:space-y-6 lg:space-y-8">
          {/* Animated Badge - Shown on all screens */}
          <div className="animate-float-in mb-2 sm:mb-2 lg:mb-4">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 sm:px-4 sm:py-2 bg-slate-900/50 backdrop-blur-md border border-pink-500/30 rounded-full">
              <span className="text-xs sm:text-xs md:text-sm text-gray-300 font-medium">{t('hero.badge')}</span>
            </div>
          </div>

          {/* Large Company Logo with Glow - Responsive scaling */}
          <div className="relative inline-block animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/40 via-pink-500/40 to-blue-500/40 blur-3xl animate-pulse-slow"></div>
            <img
              src="https://customer-assets.emergentagent.com/job_brand-showcase-98/artifacts/1mp62km8_wwi%20logo.png"
              alt={t('hero.companyName')}
              className="relative h-52 sm:h-52 md:h-56 lg:h-64 xl:h-80 w-auto mx-auto"
            />
          </div>
          
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-400 italic animate-slide-up" style={{ animationDelay: '0.5s' }}>
            {t('hero.slogan')}
          </p>

          {/* CTA Button with enhanced hover */}
          <div className="pt-4 sm:pt-4 md:pt-8 animate-slide-up" style={{ animationDelay: '0.7s' }}>
            <Button
              onClick={scrollToClients}
              size="lg"
              className="group relative bg-gradient-to-r from-blue-600 to-pink-600 hover:from-blue-700 hover:to-pink-700 text-white px-8 py-5 sm:px-10 sm:py-7 text-base sm:text-lg font-semibold rounded-xl shadow-2xl hover:shadow-pink-500/50 transition-all duration-500 transform hover:scale-110 overflow-hidden"
            >
              <span className="relative z-10 flex items-center gap-2">
                {t('hero.cta')}
                <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-pink-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Ripple effect on hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100">
                <div className="absolute inset-0 bg-white/20 rounded-full animate-ripple"></div>
              </div>
            </Button>
          </div>

          {/* Stats or Trust Indicators - Responsive */}
          <div className="pt-4 sm:pt-4 md:pt-8 lg:pt-12 grid grid-cols-3 gap-2 sm:gap-3 md:gap-6 lg:gap-8 max-w-xs sm:max-w-sm md:max-w-lg lg:max-w-2xl mx-auto animate-slide-up" style={{ animationDelay: '0.9s' }}>
            {heroStats.map((stat, index) => {
              // Get translation or fallback to capitalized key
              const translatedLabel = t(`hero.stats.${stat.key}`);
              const label = translatedLabel.startsWith('hero.stats.') 
                ? stat.key.charAt(0).toUpperCase() + stat.key.slice(1) 
                : translatedLabel;
              
              return (
                <div 
                  key={stat.id || index} 
                  className="group cursor-pointer transition-transform duration-300 hover:scale-110"
                >
                  <div className="relative p-2 sm:p-2 md:p-4 bg-slate-900/30 backdrop-blur-sm border border-slate-800 hover:border-pink-500/50 rounded-lg transition-all duration-300">
                    <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"></div>
                    <div className="relative">
                      <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-pink-400">
                        {stat.value}
                      </div>
                      <div className="text-[9px] sm:text-[10px] md:text-sm text-gray-400 mt-0.5 leading-tight">{label}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Scroll Indicator with enhanced animation - Hidden on small mobile */}
        <button
          onClick={scrollToClients}
          className="hidden sm:block absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce-enhanced group"
        >
          <div className="flex flex-col items-center text-gray-400 hover:text-pink-400 transition-colors duration-300">
            <span className="text-sm mb-2 uppercase tracking-wider group-hover:text-pink-400 transition-colors duration-300">
              {t('hero.scrollText')}
            </span>
            <div className="relative">
              <ChevronDown size={32} className="animate-bounce-slow" />
              <ChevronDown size={32} className="absolute top-0 left-0 animate-bounce-slow opacity-30" style={{ animationDelay: '0.3s' }} />
            </div>
          </div>
        </button>
      </div>

      {/* Mouse follower effect */}
      <div 
        className="pointer-events-none absolute w-96 h-96 rounded-full opacity-20 blur-3xl transition-all duration-500 ease-out"
        style={{
          background: 'radial-gradient(circle, rgba(236, 72, 153, 0.4) 0%, transparent 70%)',
          left: `${mousePosition.x * 100 - 25}%`,
          top: `${mousePosition.y * 100 - 25}%`,
        }}
      />
    </section>
  );
};
